/**
 * Mermaid Tool - 分享功能
 * static/js/mermaid/share.js
 */

import { getCurrentDiagramId } from './state.js';
import * as api from './api.js';
import { showToast, copyToClipboard, generateQRCode, showModal, hideModal, escapeHtml, formatDate } from './ui.js';

/**
 * 建立公開分享連結
 * @returns {Promise<Object|null>} 分享資料
 */
export async function createPublicShare() {
    const diagramId = getCurrentDiagramId();
    if (!diagramId) {
        showToast('請先儲存圖表', 'warning');
        return null;
    }
    
    try {
        const result = await api.createShare(diagramId, { share_type: 'public' });
        const shareUrl = window.location.origin + result.share_url;
        
        displayShareResult(shareUrl, 'public');
        showToast('分享連結已建立', 'success');
        
        return result;
    } catch (error) {
        showToast('建立分享失敗: ' + error.message, 'danger');
        return null;
    }
}

/**
 * 建立密碼保護分享連結
 * @param {string} password - 密碼
 * @returns {Promise<Object|null>} 分享資料
 */
export async function createPasswordShare(password) {
    const diagramId = getCurrentDiagramId();
    if (!diagramId) {
        showToast('請先儲存圖表', 'warning');
        return null;
    }
    
    if (!password || password.length < 4) {
        showToast('密碼至少需要 4 個字元', 'warning');
        return null;
    }
    
    try {
        const result = await api.createShare(diagramId, {
            share_type: 'password',
            password: password
        });
        const shareUrl = window.location.origin + result.share_url;
        
        displayShareResult(shareUrl, 'password');
        showToast('密碼保護分享已建立', 'success');
        
        return result;
    } catch (error) {
        showToast('建立分享失敗: ' + error.message, 'danger');
        return null;
    }
}

/**
 * 顯示分享結果
 * @param {string} shareUrl - 分享 URL
 * @param {string} shareType - 分享類型
 */
function displayShareResult(shareUrl, shareType) {
    const resultDiv = document.getElementById('shareResult');
    if (!resultDiv) return;
    
    const typeLabel = shareType === 'password' ? '🔒 密碼保護' : '🌐 公開';
    
    resultDiv.innerHTML = `
        <div class="alert alert-success">
            <div class="d-flex justify-content-between align-items-center mb-2">
                <span class="badge bg-primary">${typeLabel}</span>
                <small class="text-muted">點擊下方按鈕複製或分享</small>
            </div>
            <div class="input-group mb-2">
                <input type="text" class="form-control form-control-sm" value="${escapeHtml(shareUrl)}" readonly id="shareUrlInput">
                <button class="btn btn-outline-secondary btn-sm" type="button" id="copyShareUrlBtn">
                    <i class="fas fa-copy"></i>
                </button>
            </div>
            <div class="btn-group btn-group-sm w-100">
                <button class="btn btn-outline-primary" id="emailShareBtn">
                    <i class="fas fa-envelope me-1"></i>Email
                </button>
                <button class="btn btn-outline-primary" id="qrShareBtn">
                    <i class="fas fa-qrcode me-1"></i>QR Code
                </button>
            </div>
            <div id="shareQrCode" class="text-center mt-3" style="display: none;"></div>
        </div>
    `;
    
    resultDiv.style.display = 'block';
    
    // 綁定複製按鈕
    document.getElementById('copyShareUrlBtn')?.addEventListener('click', async () => {
        const success = await copyToClipboard(shareUrl);
        showToast(success ? '連結已複製' : '複製失敗', success ? 'success' : 'danger');
    });
    
    // 綁定 Email 按鈕
    document.getElementById('emailShareBtn')?.addEventListener('click', () => {
        const subject = encodeURIComponent('Mermaid 圖表分享');
        const body = encodeURIComponent(`請查看我分享的圖表：\n\n${shareUrl}`);
        window.open(`mailto:?subject=${subject}&body=${body}`);
    });
    
    // 綁定 QR Code 按鈕
    document.getElementById('qrShareBtn')?.addEventListener('click', () => {
        const qrDiv = document.getElementById('shareQrCode');
        if (qrDiv.style.display === 'none') {
            qrDiv.style.display = 'block';
            generateQRCode('shareQrCode', shareUrl, 150);
        } else {
            qrDiv.style.display = 'none';
        }
    });
}

/**
 * 載入圖表的分享列表
 * @returns {Promise<Array>} 分享列表
 */
export async function loadShares() {
    const diagramId = getCurrentDiagramId();
    if (!diagramId) {
        return [];
    }
    
    try {
        const result = await api.listShares(diagramId);
        displaySharesList(result.shares || []);
        return result.shares || [];
    } catch (error) {
        console.error('Load shares error:', error);
        return [];
    }
}

/**
 * 顯示分享列表
 * @param {Array} shares - 分享列表
 */
function displaySharesList(shares) {
    const listDiv = document.getElementById('existingShares');
    if (!listDiv) return;
    
    if (shares.length === 0) {
        listDiv.innerHTML = '<p class="text-muted small">尚無分享連結</p>';
        return;
    }
    
    let html = '<ul class="list-group list-group-flush">';
    
    for (const share of shares) {
        const typeIcon = share.share_type === 'password' ? '🔒' : '🌐';
        const shareUrl = window.location.origin + share.share_url;
        
        html += `
            <li class="list-group-item d-flex justify-content-between align-items-center px-0">
                <div>
                    <span class="me-1">${typeIcon}</span>
                    <small class="text-muted">${formatDate(share.created_at)}</small>
                    <span class="badge bg-secondary ms-2">${share.view_count || 0} 次瀏覽</span>
                </div>
                <div class="btn-group btn-group-sm">
                    <button class="btn btn-outline-secondary copy-share-btn" data-url="${escapeHtml(shareUrl)}">
                        <i class="fas fa-copy"></i>
                    </button>
                    <button class="btn btn-outline-danger delete-share-btn" data-id="${share.id}">
                        <i class="fas fa-trash"></i>
                    </button>
                </div>
            </li>
        `;
    }
    
    html += '</ul>';
    listDiv.innerHTML = html;
    
    // 綁定複製按鈕
    listDiv.querySelectorAll('.copy-share-btn').forEach(btn => {
        btn.addEventListener('click', async () => {
            const url = btn.dataset.url;
            const success = await copyToClipboard(url);
            showToast(success ? '連結已複製' : '複製失敗', success ? 'success' : 'danger');
        });
    });
    
    // 綁定刪除按鈕
    listDiv.querySelectorAll('.delete-share-btn').forEach(btn => {
        btn.addEventListener('click', async () => {
            if (!confirm('確定要刪除這個分享連結嗎？')) return;
            
            const shareId = parseInt(btn.dataset.id, 10);
            try {
                await api.deleteShare(shareId);
                showToast('分享連結已刪除', 'success');
                loadShares(); // 重新載入列表
            } catch (error) {
                showToast('刪除失敗: ' + error.message, 'danger');
            }
        });
    });
}

/**
 * 設定分享 Modal 事件
 */
export function setupShareModal() {
    // 顯示密碼輸入框
    document.getElementById('showPasswordInput')?.addEventListener('click', () => {
        const container = document.getElementById('passwordInputContainer');
        if (container) {
            container.style.display = container.style.display === 'none' ? 'block' : 'none';
        }
    });
    
    // 建立公開分享
    document.getElementById('createPublicShareBtn')?.addEventListener('click', () => {
        createPublicShare();
    });
    
    // 建立密碼分享
    document.getElementById('createPasswordShareBtn')?.addEventListener('click', () => {
        const passwordInput = document.getElementById('sharePassword');
        if (passwordInput) {
            createPasswordShare(passwordInput.value);
        }
    });
    
    // Modal 打開時載入現有分享
    document.getElementById('shareModal')?.addEventListener('show.bs.modal', () => {
        loadShares();
        // 重置分享結果區域
        const resultDiv = document.getElementById('shareResult');
        if (resultDiv) {
            resultDiv.style.display = 'none';
        }
    });
}
